import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SETUP_BASE_PATH } from "./wizard/wizardSteps";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  btnPrimaryClass,
  btnSecondaryClass,
  btnGhostClass,
  formLabelClass,
  formControlClass,
  requiredMarkClass,
  formGridClass,
  formFullWidthClass,
  alertErrorClass,
  infoGridClass,
  statsGridClass,
  spinnerClass,
} from "../dashboardStyles";
import {
  GetOrganizationAction,
  UpdateOrganizationAction,
  GetResourcesAction,
  GetServicesAction,
  GetTotalBookingsCountAction,
} from "../../../store/action";
import { notify, notifySaveError } from "../../../utils/notify";

/** One read-only label/value pair in the business information grid. */
const InfoItem = ({ label, value }) => (
  <div>
    <p className={formLabelClass}>{label}</p>
    <p className="m-0 text-fluid-sm break-words text-ink-900">{value || "—"}</p>
  </div>
);

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

/** One labelled input in the edit form. */
const EditField = ({
  id,
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
  className = "",
}) => (
  <div className={className}>
    <label htmlFor={id} className={formLabelClass}>
      {label} {required && <span className={requiredMarkClass}>*</span>}
    </label>
    <input
      type={type}
      id={id}
      name={id}
      value={value ?? ""}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className={formControlClass}
    />
  </div>
);

EditField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};

/** A single figure in the Quick Stats grid. */
const StatCard = ({ icon, value, label }) => (
  <div className="flex items-center gap-4 rounded-lg border border-ink-200 bg-ink-50 p-4 transition hover:border-brand-600 hover:bg-white hover:shadow-card">
    <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white">
      <i className={`bi ${icon} text-xl`} />
    </div>
    <div className="min-w-0">
      <h4 className="m-0 text-2xl font-bold text-ink-900">{value}</h4>
      <p className="m-0 text-xs text-ink-500">{label}</p>
    </div>
  </div>
);

StatCard.propTypes = {
  icon: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

/**
 * Organization Page - Manage business profile and settings
 */
const Organization = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resourceCount, setResourceCount] = useState(0);
  const [serviceCount, setServiceCount] = useState(0);
  const [bookingCount, setBookingCount] = useState(0);

  useEffect(() => {
    fetchProfile();
    fetchCounts();
  }, []);

  const fetchCounts = async () => {
    try {
      const [resources, services, bookingsCount] = await Promise.all([
        GetResourcesAction(),
        GetServicesAction(),
        GetTotalBookingsCountAction(),
      ]);
      setResourceCount(resources.length);
      setServiceCount(services.length);
      setBookingCount(bookingsCount?.totalBookings ?? 0);
    } catch (err) {
      console.error("Error fetching counts:", err);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await GetOrganizationAction();
      setProfile(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching organization:", err);

      if (err.response && err.response.status === 404) {
        // No organization yet for this user
        setProfile(null);
        setError(null);
      } else if (err.response && err.response.status === 401) {
        // Unauthorized - redirect to login or show error
        setError(t("Dashboard.Organization.unauthorizedError"));
        setProfile(null);
      } else {
        // Other errors
        setProfile(null);
        setError(t("Dashboard.Organization.loadError"));
      }
    } finally {
      setLoading(false);
    }
  };

  // The wizard is its own set of pages; it resumes on whichever step the
  // business had reached (Organization.SetupStep).
  const openWizard = () => navigate(SETUP_BASE_PATH);

  const handleEditClick = () => {
    setEditForm({
      businessName: profile.businessName || "",
      companyName: profile.companyName || "",
      registrationCode: profile.registrationCode || "",
      county: profile.county || "",
      city: profile.city || "",
      address: profile.address || "",
      phoneNumber: profile.phoneNumber || "",
      phoneCountryCode: profile.phoneCountryCode || "40",
    });
    setIsEditing(true);
    setError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
    setError(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updatedData = await UpdateOrganizationAction(editForm);
      setProfile(updatedData);
      setIsEditing(false);
      setEditForm({});
      notify.success(t("Dashboard.Organization.updated"));
    } catch (err) {
      console.error("Error updating organization:", err);
      notifySaveError(err, t("Dashboard.Organization.updateError"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div
          role="status"
          aria-label={t("Dashboard.Organization.loading")}
          className={spinnerClass}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>{t("Dashboard.Organization.title")}</h1>
        <p className={pageSubtitleClass}>
          {t("Dashboard.Organization.subtitle")}
        </p>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {profile && !profile.isSetupComplete && (
        <div
          className={`${cardClass} mb-6 flex flex-col items-start gap-4 border-l-4 border-l-amber-500 sm:flex-row sm:items-center sm:justify-between`}
          role="alert"
        >
          <div className="flex items-start gap-3">
            <i className="bi bi-lock-fill mt-0.5 shrink-0 text-xl text-amber-600" />
            <div>
              <h3 className="m-0 text-fluid-sm font-semibold text-ink-900">
                {t("Dashboard.Organization.setupIncompleteTitle")}
              </h3>
              <p className="m-0 mt-1 text-fluid-sm text-ink-500">
                {t("Dashboard.Organization.setupIncompleteSubtitle")}
              </p>
            </div>
          </div>
          {/* Reopens the wizard rather than flipping the completion flag
              directly. The flag must only ever be set by the wizard's own
              final step, which requires at least one resource and one
              service before it lets you get there — a direct "mark done"
              shortcut here could (and did) leave setup flagged complete with
              nothing configured behind it. */}
          <button
            className={`${btnPrimaryClass} shrink-0`}
            onClick={openWizard}
          >
            {t("Dashboard.Organization.continueSetup")}
          </button>
        </div>
      )}

      {!profile ? (
        <div
          className={`${cardClass} flex flex-col items-center px-5 py-14 text-center`}
        >
          <div className="mb-5 flex size-20 items-center justify-center rounded-full bg-brand-50">
            <i className="bi bi-building text-4xl text-brand-600" />
          </div>
          <h2 className="m-0 mb-6 text-xl font-semibold text-ink-900">
            {t("Dashboard.Organization.noProfileTitle")}
          </h2>
          <button className={btnPrimaryClass} onClick={openWizard}>
            <i className="bi bi-plus-circle" />
            {t("Dashboard.Organization.completeSetup")}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <h3 className={cardTitleClass}>
                {t("Dashboard.Organization.businessInformation")}
              </h3>
              {!isEditing ? (
                <button className={btnSecondaryClass} onClick={handleEditClick}>
                  <i className="bi bi-pencil" />
                  {t("Dashboard.Organization.edit")}
                </button>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    className={btnGhostClass}
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    {t("Dashboard.Organization.cancel")}
                  </button>
                  <button
                    type="submit"
                    form="edit-organization-form"
                    className={btnPrimaryClass}
                    disabled={saving}
                  >
                    {saving
                      ? t("Dashboard.Organization.saving")
                      : t("Dashboard.Organization.saveChanges")}
                  </button>
                </div>
              )}
            </div>
            <div className={cardBodyClass}>
              {!isEditing ? (
                <div className={infoGridClass}>
                  <InfoItem
                    label={t("Dashboard.Organization.businessNameLabel")}
                    value={profile.businessName}
                  />
                  <InfoItem
                    label={t("Dashboard.Organization.companyNameLabel")}
                    value={profile.companyName}
                  />
                  <InfoItem
                    label={t("Dashboard.Organization.registrationCodeLabel")}
                    value={profile.registrationCode}
                  />
                  <InfoItem
                    label={t("Dashboard.Organization.countyLabel")}
                    value={profile.county}
                  />
                  <InfoItem
                    label={t("Dashboard.Organization.cityLabel")}
                    value={profile.city}
                  />
                  <InfoItem
                    label={t("Dashboard.Organization.addressLabel")}
                    value={profile.address}
                  />
                  {profile.phoneNumber && (
                    <InfoItem
                      label={t("Dashboard.Organization.phoneNumberLabel")}
                      value={`${
                        profile.phoneCountryCode
                          ? `+${profile.phoneCountryCode} `
                          : ""
                      }${profile.phoneNumber}`}
                    />
                  )}
                </div>
              ) : (
                <form
                  id="edit-organization-form"
                  className={formGridClass}
                  onSubmit={handleSaveEdit}
                >
                  <EditField
                    id="businessName"
                    label={t("Dashboard.Organization.businessNameLabel")}
                    value={editForm.businessName}
                    onChange={handleFormChange}
                    required
                  />
                  <EditField
                    id="companyName"
                    label={t("Dashboard.Organization.companyNameLabel")}
                    value={editForm.companyName}
                    onChange={handleFormChange}
                    required
                  />
                  <EditField
                    id="registrationCode"
                    label={t("Dashboard.Organization.registrationCodeLabel")}
                    value={editForm.registrationCode}
                    onChange={handleFormChange}
                    required
                  />
                  <EditField
                    id="county"
                    label={t("Dashboard.Organization.countyLabel")}
                    value={editForm.county}
                    onChange={handleFormChange}
                    required
                  />
                  <EditField
                    id="city"
                    label={t("Dashboard.Organization.cityLabel")}
                    value={editForm.city}
                    onChange={handleFormChange}
                    required
                  />
                  <EditField
                    id="address"
                    label={t("Dashboard.Organization.addressLabel")}
                    value={editForm.address}
                    onChange={handleFormChange}
                    required
                    className={formFullWidthClass}
                  />
                  <EditField
                    id="phoneCountryCode"
                    label={t("Dashboard.Organization.countryCodeLabel")}
                    value={editForm.phoneCountryCode}
                    onChange={handleFormChange}
                    placeholder={t("Dashboard.Organization.countryCodePlaceholder")}
                  />
                  <EditField
                    id="phoneNumber"
                    label={t("Dashboard.Organization.phoneNumberLabel")}
                    type="tel"
                    value={editForm.phoneNumber}
                    onChange={handleFormChange}
                    placeholder={t("Dashboard.Organization.phoneNumberPlaceholder")}
                  />
                </form>
              )}
            </div>
          </div>

          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <h3 className={cardTitleClass}>
                {t("Dashboard.Organization.quickStats")}
              </h3>
            </div>
            <div className={cardBodyClass}>
              <div className={statsGridClass}>
                <StatCard
                  icon="bi-calendar-check"
                  value={bookingCount}
                  label={t("Dashboard.Organization.totalBookings")}
                />
                <StatCard
                  icon="bi-briefcase"
                  value={serviceCount}
                  label={t("Dashboard.Organization.services")}
                />
                <StatCard
                  icon="bi-people"
                  value={resourceCount}
                  label={t("Dashboard.Organization.resources")}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organization;
